// Shobu Arena web app: the header, routing, and the home, invite and admin views.
import type {
  AdminPlayer,
  AdminServer,
  Badges,
  DevPlayersCreated,
  Invite,
  LineupPick,
  MatchStarted,
  Player,
  RollResult,
  RollsGranted,
  TournamentPage,
} from '../src/api.js';
import { Duels } from './duels.js';
import { Lineups } from './lineups.js';
import { Page } from './page.js';
import { Patches } from './patches.js';
import { Session } from './session.js';
import { Tooltip } from './tooltip.js';
import { Tournament } from './tournament.js';
import { type Html, Ui } from './ui.js';

const { api, h, setHtml, el, elOf, toast, attempt } = Ui;
const { catalog, me } = Session;

type View = () => Promise<void> | void;

const renderMe = (): void => {
  const box = el('#me');
  if (!me.player) {
    setHtml(box, h`<a class="btn small" href="/auth/login">Log in with Discord</a>`);
    return;
  }
  const { user, currency, servers, serverId } = me.player;
  const hasServerPick = servers.length > 1;
  const serverPick = hasServerPick
    ? h`<select class="server-pick" id="serverPick" aria-label="Server">${servers.map(
        s => h`<option value="${s.id}" ${s.id === serverId ? 'selected' : ''}>${s.name}</option>`,
      )}</select>`
    : '';
  setHtml(
    box,
    h`${serverPick}<span class="coins" id="balance" title="${catalog.currency.name}">${Session.coins(currency)}</span>
    ${user.avatar ? h`<img src="${user.avatar}" alt="">` : h`<span class="avatar"></span>`}
    <span>${user.name}</span> <button class="link-btn" id="logout">Log out</button>`,
  );
  if (hasServerPick) {
    const pick = elOf(HTMLSelectElement, '#serverPick');
    pick.onchange = () => attempt(() => Session.switchServer(pick.value));
  }
  el('#logout').onclick = async () => {
    await api('/auth/logout', { method: 'POST' });
    location.reload();
  };
};

const notYet = (): void =>
  setHtml(
    Page.view,
    h`<div class="panel stack"><h2>Your server isn't here yet</h2>
    <p class="muted">Shobu Arena is played inside Discord servers, and none of yours has joined. Ask whoever runs
      Shobu Arena to add your server, then log in again.</p>
    <div><a class="btn" href="/auth/login">Log in again</a></div></div>`,
  );

const needLogin = (): void =>
  setHtml(
    Page.view,
    h`<div class="panel stack"><h2>Log in to play</h2>
    <p class="muted">Your lineup and tournaments are tied to your Discord account.</p>
    <a class="btn" href="/auth/login">Log in with Discord</a></div>`,
  );

const devLoginHtml = (): Html | '' =>
  me.devLogin
    ? h`<form class="row dev-login" id="devLogin">
        <input name="n" placeholder="Test player name" required class="field">
        <button class="btn ghost small">Dev login</button></form>`
    : '';

/** An `outsider` logs in as if they were in no Shobu Arena server. */
const bindDevLogin = (outsider = false): void => {
  if (!me.devLogin) return;
  Page.onSubmit('#devLogin', input => {
    location.href = `/auth/dev?name=${encodeURIComponent(input.value)}${outsider ? '&outsider=1' : ''}`;
  });
};

const welcome = (): void => {
  setHtml(
    Page.view,
    h`<section class="stack">
      <h1>Pick.<br>Scheme.<br>Win the tournament.</h1>
      <p class="muted">Build a team of three from the whole roster and take on your friends. Anyone can start a tournament, and everyone watches the fights live.</p>
      <a class="btn" href="/auth/login">Log in with Discord</a>
      ${devLoginHtml()}
    </section>`,
  );
  bindDevLogin();
};

const payoutHtml = (amount: number): Html => {
  const big = amount >= 50;
  return h`<div class="payout ${big ? 'big' : ''}">
    <div class="amount">${Session.coins(amount)}</div>
    <div class="muted">${big ? 'Big payout!' : `${catalog.currency.name} in the bank`}</div>
  </div>`;
};

const homeHtml = (player: Player): Html =>
  h`<div class="cards">
    <section class="panel stack" id="tournamentCard"></section>
    <section class="panel stack">
      <div class="muted"><span id="rolls">${player.rollsLeft}</span> rolls left today</div>
      <button class="btn" id="roll" ${player.rollsLeft ? '' : 'disabled'}>Roll</button>
      <p class="muted">Every roll pays out ${catalog.currency.name}.</p>
      <div class="reveal" id="reveal"></div>
    </section>
    <section class="panel stack" id="last"></section>
    <section class="panel stack">
      <h3>Training Bot</h3>
      <p class="muted">A practice fight against a random team. Test your lineup, nothing is at stake.</p>
      ${player.lineups.length > 1 ? Lineups.chipsHtml(player.lineups, Session.activeLineup(player)?.id ?? null) : ''}
      <div><button class="btn ghost" id="train" ${player.lineups.length ? '' : 'disabled'}>Train</button></div>
    </section>
  </div>`;

/** Keeps the tournament card and the reigning champion up to date. Players without a server get neither. */
const showTournament = (player: Player): void => {
  if (!player.serverId) {
    setHtml(el('#tournamentCard'), Tournament.noServerHtml);
    el('#last').hidden = true;
    return;
  }
  let page: TournamentPage | null = null;
  const tick = Tournament.joinCountdown(() => page);
  const load = async () => {
    page = await Tournament.fetchPage();
    if (Page.renderIfChanged(el('#tournamentCard'), Tournament.cardHtml(page.tournament))) tick();
    Page.renderIfChanged(el('#last'), Tournament.championHtml(page.champion));
  };
  void attempt(load);
  Page.poll(load, 5000);
};

const bindRoll = (player: Player): void => {
  const rollBtn = elOf(HTMLButtonElement, '#roll');
  rollBtn.onclick = () =>
    attempt(async () => {
      rollBtn.disabled = true;
      try {
        const r = await api<RollResult>('/api/roll', { method: 'POST' });
        player.rollsLeft = r.rollsLeft;
        setHtml(el('#reveal'), payoutHtml(r.amount));
        el('#rolls').textContent = String(r.rollsLeft);
        Session.setCurrency(r.currency);
      } finally {
        rollBtn.disabled = player.rollsLeft <= 0;
      }
    });
};

const bindTraining = (player: Player): void => {
  const trainBtn = el('#train');
  trainBtn.onclick = () =>
    attempt(async () => {
      const body: LineupPick = { lineupId: Session.activeLineup(player)?.id };
      Page.openMatch(await api<MatchStarted>('/api/training', { method: 'POST', body }));
    });
  const card = trainBtn.closest('section');
  if (!card) return;
  Lineups.bindChips(card, id => {
    Session.setActiveLineup(id);
    card
      .querySelectorAll('[data-lineup]')
      .forEach(b => b.setAttribute('aria-pressed', String(b.getAttribute('data-lineup') === String(id))));
  });
};

const home = (): void => {
  const player = me.player;
  if (!player) return welcome();
  setHtml(Page.view, homeHtml(player));
  showTournament(player);
  bindTraining(player);
  bindRoll(player);
};

/** Where a friend link lands anyone who isn't logged in. */
const invite = async (): Promise<void> => {
  if (me.player) {
    location.hash = '#/duel';
    return;
  }
  const from = await api<Invite | null>('/api/invite');
  setHtml(
    Page.view,
    from
      ? h`<section class="panel stack"><h2>${from.name} wants to duel you</h2>
        <p class="muted">Shobu Arena is a battle game: build a team of three fighters and take on your friends. Log in
          with Discord to become friends with ${from.name} and answer the challenge.</p>
        <div><a class="btn" href="/auth/login">Log in with Discord</a></div>${devLoginHtml()}</section>`
      : h`<section class="panel stack"><h2>This friend link doesn't work</h2>
        <p class="muted">Friend links last a week. Ask your friend for their new one.</p></section>`,
  );
  bindDevLogin(true);
};

const DEFAULT_BOT_NAMES = 'Bot 1, Bot 2, Bot 3, Bot 4';

const adminHtml = (players: AdminPlayer[], servers: AdminServer[]): Html =>
  h`<h2>Admin</h2>
      <section class="panel stack">
        <h3>Servers</h3>
        <p class="muted">Members of an active server can log in, and tournaments only pit members of the same server.
          Add servers with <code>npm run server:add</code>. Names and icons update from Discord whenever a member logs in.</p>
        <div class="list">${servers.map(
          s => h`<div class="list-item">${Page.avatarHtml(s.icon)}<div class="grow">${s.name}
            <div class="muted sub">${s.members} members · ${s.hasWebhook ? 'Announces in Discord' : 'No webhook'}${s.active ? '' : ' · Inactive'}</div></div>
            <button class="btn ghost small" data-active="${s.id}" data-to="${String(!s.active)}">${s.active ? 'Deactivate' : 'Activate'}</button></div>`,
        )}</div>
      </section>
      <section class="panel stack">
        <h3>Players</h3>
        <p class="muted">${players.filter(p => p.hasLineup).length} of ${players.length} players have a lineup.</p>
        <div class="list">${players.map(
          p => h`<div class="list-item"><div class="grow">${p.name}
            <div class="muted sub">${p.hasLineup ? 'Lineup ready' : 'No lineup yet'}</div></div></div>`,
        )}</div>
      </section>
      <section class="panel stack">
        <h3>Give rolls</h3>
        <p class="muted">Adds bonus rolls to all ${players.length} players. Bonus rolls don't expire at midnight.</p>
        <form class="row tight" id="giveRolls">
          <input class="field" name="count" type="number" min="1" max="100" value="5" aria-label="Rolls per player" required>
          <button class="btn ghost small">Give everyone rolls</button>
        </form>
      </section>
      ${
        me.devMode
          ? h`<section class="panel stack">
        <h3>Local testing</h3>
        <p class="muted">Only reachable while DEV_MODE is on. Test players get a saved lineup and join every tournament
          on their own. Reusing a name reuses that player.</p>
        <form class="row tight" id="addPlayers">
          <input class="field" name="names" value="${DEFAULT_BOT_NAMES}" aria-label="Test player names, comma separated" required>
          <button class="btn ghost small">Add test players</button>
        </form>
      </section>`
          : ''
      }`;

const admin = async (player: Player): Promise<void> => {
  if (!player.user.isAdmin) {
    setHtml(Page.view, h`<h2>Admin</h2><div class="panel"><p class="muted">Admins only.</p></div>`);
    return;
  }

  const render = async () => {
    const [players, servers] = await Promise.all([
      api<AdminPlayer[]>('/api/admin/players'),
      api<AdminServer[]>('/api/admin/servers'),
    ]);
    setHtml(Page.view, adminHtml(players, servers));

    Page.view.querySelectorAll<HTMLButtonElement>('[data-active]').forEach(btn => {
      btn.onclick = () =>
        attempt(async () => {
          const body = { active: btn.dataset.to === 'true' };
          await api(`/api/admin/servers/${btn.dataset.active}/active`, { method: 'POST', body });
          await render();
        });
    });
    Page.onSubmit('#giveRolls', async input => {
      const r = await api<RollsGranted>('/api/admin/rolls', { method: 'POST', body: { count: input.valueAsNumber } });
      player.rollsLeft += r.count;
      toast(`Gave ${r.count} rolls to ${r.players} players`);
    });
    if (me.devMode)
      Page.onSubmit('#addPlayers', async input => {
        const names = input.value.split(',');
        const r = await api<DevPlayersCreated>('/api/dev/players', { method: 'POST', body: { names } });
        toast(`${r.players.length} test players ready`);
        await render();
      });
  };
  await render();
};

/** Views that need a logged-in player get it passed in; others see the login prompt instead. */
const loggedIn =
  (view: (player: Player) => Promise<void> | void): View =>
  () =>
    me.player ? view(me.player) : needLogin();

const views: Record<string, View> = {
  home,
  roster: Lineups.roster,
  lineup: loggedIn(Lineups.editor),
  tournament: loggedIn(Tournament.view),
  duel: loggedIn(Duels.view),
  patches: Patches.view,
  invite,
  admin: loggedIn(admin),
  'not-yet': notYet,
};

/** Links from Discord name their server, so they open where the tournament is. */
const linkedServer = (query: string): string | null => {
  const linked = new URLSearchParams(query).get('server');
  const player = me.player;
  return linked && player && linked !== player.serverId && player.servers.some(s => s.id === linked) ? linked : null;
};

const route = async (): Promise<void> => {
  Page.reset();
  const [path, query = ''] = location.hash.replace(/^#\//, '').split('?');
  const name = path || 'home';
  const linked = linkedServer(query);
  if (linked) {
    await attempt(() => Session.switchServer(linked));
    return;
  }
  document
    .querySelectorAll('#nav a')
    .forEach(a => a.classList.toggle('active', a.getAttribute('href') === `#/${name === 'home' ? '' : name}`));
  try {
    await (views[name] ?? home)();
  } catch (err) {
    setHtml(Page.view, h`<div class="panel">${err instanceof Error ? err.message : String(err)}</div>`);
  }
};

const topbar = el('.topbar');
new ResizeObserver(() => document.documentElement.style.setProperty('--topbar-h', `${topbar.offsetHeight}px`)).observe(
  topbar,
);
Tooltip.attach(Page.view, Tooltip.textTip);
renderMe();
el('#navAdmin').hidden = !me.player?.user.isAdmin;
Session.setBadges(me.badges);
if (me.player) window.setInterval(() => void api<Badges>('/api/badges').then(Session.setBadges, () => undefined), 5000);
window.addEventListener('hashchange', () => void route());
void route();
